import { Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { MaterialModule } from "src/app/app.materials";
import { CommonModule } from "@angular/common";

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface TreemapTile extends Rect {
  ticker: string;
  full: string;
  label: string;
  background: string;
  strong: boolean;
  small: boolean;
  tiny: boolean;
}

@Component({
  selector: "app-heatmap-chart",
  templateUrl: "./heatmap-chart.component.html",
  styleUrls: ["./heatmap-chart.component.scss"],
  imports: [MaterialModule, CommonModule],
  standalone: true,
})
export class HeatmapChartComponent implements OnInit, OnChanges {
  @Input() dataRaw: any = {};
  @Input() allowedTickers: any[] = [];
  // Kept for backwards compatibility with existing template bindings.
  @Input() fullHeight = false;
  @Input() extraOptions = {};
  @Input() hideTitle = false;
  @Input() isPrint = false;
  @Input() title: string;

  tiles: TreemapTile[] = [];

  ngOnInit(): void {
    this.build();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.dataRaw || changes.allowedTickers) {
      this.build();
    }
  }

  private build() {
    const raw = Array.isArray(this.dataRaw) ? this.dataRaw : [];
    const items = raw
      .filter((d) => d && d.ticker !== "ZERO--" && Number(d.risk) > 0)
      .map((d) => ({
        ticker: (d.displayTicker ?? d.ticker ?? "").toString(),
        full: (d.fullName ?? (typeof d.name === "string" ? d.name.split(":")[0] : "") ?? "").toString(),
        value: Number(d.risk) || 0,
      }))
      .sort((a, b) => b.value - a.value);

    if (!items.length) {
      this.tiles = [];
      return;
    }

    const values = items.map((i) => i.value);
    const max = Math.max(...values);
    const min = Math.min(...values);
    const rects = this.squarify(values, 0, 0, 100, 100);

    this.tiles = items.map((item, idx) => {
      const t = max === min ? 0.65 : (item.value - min) / (max - min);
      const pct = Math.round(45 + t * 55);
      const rect = rects[idx];
      const minSide = Math.min(rect.w, rect.h);
      return {
        ...rect,
        ticker: item.ticker,
        full: item.full,
        label: item.value.toFixed(2) + "%",
        background: `color-mix(in srgb, var(--accent) ${pct}%, var(--surface-2))`,
        strong: pct >= 58,
        small: minSide < 18,
        tiny: minSide < 11,
      };
    });
  }

  /** Squarified treemap (Bruls, Huizing, van Wijk) — areas proportional to value, aspect ratios near 1. */
  private squarify(values: number[], x0: number, y0: number, width: number, height: number): Rect[] {
    const total = values.reduce((a, b) => a + b, 0) || 1;
    const order = values.map((_, i) => i).sort((a, b) => values[b] - values[a]);
    const area = width * height;
    const scaled = order.map((i) => (values[i] * area) / total);
    const rects: Rect[] = new Array(values.length);

    let x = x0;
    let y = y0;
    let w = width;
    let h = height;
    let row: number[] = [];
    let rowIdx: number[] = [];

    const worst = (areas: number[], side: number) => {
      if (!areas.length) {
        return Infinity;
      }
      const sum = areas.reduce((a, b) => a + b, 0);
      const max = Math.max(...areas);
      const minA = Math.min(...areas);
      const s2 = sum * sum;
      const side2 = side * side;
      return Math.max((side2 * max) / s2, s2 / (side2 * minA));
    };

    const layoutRow = () => {
      const sum = row.reduce((a, b) => a + b, 0);
      if (w >= h) {
        const colW = sum / h;
        let cy = y;
        for (let k = 0; k < row.length; k++) {
          const cellH = row[k] / colW;
          rects[rowIdx[k]] = { x, y: cy, w: colW, h: cellH };
          cy += cellH;
        }
        x += colW;
        w -= colW;
      } else {
        const rowH = sum / w;
        let cx = x;
        for (let k = 0; k < row.length; k++) {
          const cellW = row[k] / rowH;
          rects[rowIdx[k]] = { x: cx, y, w: cellW, h: rowH };
          cx += cellW;
        }
        y += rowH;
        h -= rowH;
      }
      row = [];
      rowIdx = [];
    };

    for (let oi = 0; oi < order.length; oi++) {
      const a = scaled[oi];
      const side = Math.min(w, h);
      if (row.length === 0 || worst([...row, a], side) <= worst(row, side)) {
        row.push(a);
        rowIdx.push(order[oi]);
      } else {
        layoutRow();
        row.push(a);
        rowIdx.push(order[oi]);
      }
    }
    if (row.length) {
      layoutRow();
    }
    return rects;
  }
}
